import { FormEvent, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { apiFetch } from "../api/client";
import { queryClient } from "../app/queryClient";
import { Panel } from "../components/Panel";
import type { AllowedTopic } from "../types";

const emptyTopic = {
  name: "",
  description: "",
  allowed_examples: [] as string[],
  blocked_examples: [] as string[],
  is_active: true,
};

export function AllowedTopicsPage() {
  const [form, setForm] = useState(emptyTopic);
  const { data } = useQuery({
    queryKey: ["allowed-topics"],
    queryFn: () => apiFetch<{ data: AllowedTopic[] }>("/api/allowed-topics"),
  });
  const createTopic = useMutation({
    mutationFn: () => apiFetch("/api/allowed-topics", { method: "POST", body: form }),
    onSuccess: () => {
      setForm(emptyTopic);
      queryClient.invalidateQueries({ queryKey: ["allowed-topics"] });
    },
  });
  const deleteTopic = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/allowed-topics/${id}`, { method: "DELETE", body: {} }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["allowed-topics"] }),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    createTopic.mutate();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold">Temi consentiti</h2>
        <p className="mt-1 text-slate-400">Il classificatore autorizza il bot solo su questi argomenti.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1fr_420px]">
        <Panel>
          <div className="space-y-4">
                        {(data?.data ?? []).map((topic) => (
              <div key={topic.id} className="rounded-2xl border border-white/10 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-lg font-semibold">{topic.name}</p>
                    <p className="mt-2 text-sm leading-6 text-slate-400">{topic.description}</p>
                    <p className="mt-3 text-xs uppercase tracking-wide text-emerald-300">
                      {topic.is_active ? "Attivo" : "Disattivo"}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteTopic.mutate(topic.id)}
                    className="rounded-xl border border-red-400/20 p-2 text-red-300 hover:bg-red-400/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel>
          <form onSubmit={submit} className="space-y-4">
            <h3 className="text-lg font-semibold">Nuovo tema</h3>
            <input
              className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3"
              placeholder="Nome tema"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
            <textarea
              className="h-36 w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3"
              placeholder="Descrizione e limiti del tema"
              value={form.description}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
            <button className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-400 px-4 py-3 font-semibold text-slate-950 hover:bg-emerald-300">
              <Plus className="h-4 w-4" />
              Aggiungi tema
            </button>
          </form>
        </Panel>
      </div>
    </div>
  );
}
